import {Badge, Box, Chip, List, ListItemButton, ListItemIcon, Typography} from '@mui/material'
import {ChatBubbleOutline, ChevronRight} from '@mui/icons-material'
import {Fragment} from 'react'
import {useLocation, useParams} from 'react-router-dom'
import {useContent} from '../../logic'
import {useGetOrder} from '../../logic/useApiQuery'
import {PortalNotificationReference} from '../../types'
import {getMostRecentActivityDate, getNotificationsInfo, hasUnreadReplies} from '../../utils'
import {LinkWithSearchParams, LoadingScreen} from '../shared'
import dayjs from 'dayjs'

function MessageListItem({
                           notification,
                           isLast,
                         }: {
  notification: PortalNotificationReference;
  isFirst: boolean;
  isLast: boolean;
}) {
  const isUnread = !notification.ReadReceipt || hasUnreadReplies(notification)
  const unreadReplies = hasUnreadReplies(notification)
  const date = dayjs(getMostRecentActivityDate(notification)).format('MMM D, YYYY h:mm A')

  return (
    <ListItemButton
      component={LinkWithSearchParams}
      to={notification.NotificationId}
      sx={{
        borderBottom: isLast ? 'none' : '1px solid #ddd',
        p: 2,
        '&:hover': {color: 'initial', backgroundColor: '#ddd'},
      }}
    >
      {isUnread && (
        <Box
          sx={{
            width: 8,
            height: 8,
            bgcolor: 'primary.main',
            mr: 1.5,
            flexShrink: 0,
            alignSelf: 'center',
          }}
        />
      )}
      <Box sx={{flex: 1, minWidth: 0}}>
        <Typography noWrap fontWeight={isUnread ? 700 : 400}>
          {notification.NotificationTitle}
        </Typography>
        <Typography noWrap variant="body2" color="text.secondary">
          {notification.Content}
        </Typography>
        <Typography variant="teeny" sx={{letterSpacing: '0.1px', color: '#ABABAF'}}>
          {date}
        </Typography>
      </Box>
      {(notification.Replies?.length ?? 0) > 0 && (
        <Chip
          label={`${notification.Replies!.length} ${notification.Replies!.length > 1 ? 'Replies' : 'Reply'}`}
          size="small"
          sx={{
            ml: 1,
            flexShrink: 0,
            bgcolor: unreadReplies ? 'primary.main' : 'grey.300',
            color: unreadReplies ? 'primary.contrastText' : 'text.secondary',
            fontWeight: 600,
            height: 20,
            fontSize: '0.7rem',
            '.MuiChip-label': { px: '6px' },
          }}
        />
      )}
      <ListItemIcon sx={{ml: 1, minWidth: 0, color: 'primary.main'}}>
        <ChevronRight/>
      </ListItemIcon>
    </ListItemButton>
  )
}

export function MessagingPage() {
  const {closingId} = useParams()
  const {search} = useLocation()
  const uiContent = useContent()
  const {data: globalOrderData, isLoading} = useGetOrder(closingId ?? '', search ?? '')

  if (isLoading || !globalOrderData) return <LoadingScreen/>

  const notificationsInfo = getNotificationsInfo(
    globalOrderData.Notifications,
    uiContent,
    globalOrderData,
  )
  const {messages, unreadMessages} = notificationsInfo
  const unreadCount = unreadMessages.length

  return (
    <Box sx={{width: '100%'}}>
      <Box sx={{bgcolor: 'white', borderRadius: '16px', overflow: 'hidden'}}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, borderBottom: '1px solid #ddd'}}>
          <Badge badgeContent={unreadCount} color="primary">
            <ChatBubbleOutline sx={{color: 'primary.main'}}/>
          </Badge>
          <Typography variant="h5" sx={{fontWeight: 500}}>
            Messages
          </Typography>
        </Box>
        {messages.length === 0 ? (
          <Box sx={{p: 2, textAlign: 'center'}}>
            <Typography color="text.secondary">No messages</Typography>
          </Box>
        ) : (
          <List disablePadding>
            {messages.map((notification, index) => (
              <Fragment key={notification.NotificationId}>
                <MessageListItem
                  notification={notification}
                  isFirst={index === 0}
                  isLast={index === messages.length - 1}
                />
              </Fragment>
            ))}
          </List>
        )}
      </Box>
    </Box>
  )
}
